import { useState } from "react";
import { useParams } from "react-router-dom";
import { Pencil } from "lucide-react";

import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useParty } from "@/features/kyc/api/use-party";
import { CompanyProfileCard, PartyFormDialog, PartySanctionsOverride } from "@/features/kyc/components";

/** Détail d'une identité (GET /Parties/{id}) — PII en clair, en NoTranslate. */
export function PartyDetailPage() {
  const { t } = useTypedTranslation();
  const { partyId = "" } = useParams();
  const { data: party, isLoading, error, refetch } = useParty(partyId);
  const [editOpen, setEditOpen] = useState(false);
  const backButton = (
    <PageBackButton to="/app/kyc/parties" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !party) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("reload")}
          </Button>
        </div>
      </section>
    );
  }

  const fields: { label: string; value?: string }[] = [
    { label: t("kyc.parties.form.firstName"), value: party.firstName },
    { label: t("kyc.parties.form.lastName"), value: party.lastName },
    { label: t("kyc.parties.form.dateOfBirth"), value: party.dateOfBirth },
    { label: t("kyc.parties.form.nationality"), value: party.nationality },
    { label: t("kyc.parties.form.phone"), value: party.phone },
    { label: t("kyc.parties.form.email"), value: party.email },
    { label: t("kyc.parties.form.nationalIdNumber"), value: party.nationalIdNumber },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            <NoTranslate as="span">
              {[party.firstName, party.lastName].filter(Boolean).join(" ") ||
                party.id}
            </NoTranslate>
          </h1>
        </div>
        <Button variant="outline" onClick={() => setEditOpen(true)}>
          <Pencil className="mr-2 h-4 w-4" />
          {t("kyc.parties.actions.edit")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <NoTranslate as="span">
              <Badge variant="secondary">{party.partyType}</Badge>
            </NoTranslate>
            {party.kycStatus ? (
              <NoTranslate as="span">
                <Badge variant="outline">{party.kycStatus}</Badge>
              </NoTranslate>
            ) : null}
            {party.sanctionsScreeningStatus ? (
              <NoTranslate as="span">
                <Badge variant="outline">{party.sanctionsScreeningStatus}</Badge>
              </NoTranslate>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map(({ label, value }) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="font-medium">
                  <NoTranslate as="span">{value || "—"}</NoTranslate>
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* Profil entreprise — rendu null si partyType !== COMPANY (2.4) ;
          la lecture est réservée aux rôles back-office (§6.2). */}
      <RequireRole roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}>
        <CompanyProfileCard partyId={party.id} partyType={party.partyType} />
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("kyc.parties.screening.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PartySanctionsOverride
              partyId={party.id}
              currentStatus={party.sanctionsScreeningStatus}
            />
          </CardContent>
        </Card>
      </RequireRole>

      <PartyFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        party={party}
      />
    </section>
  );
}
